import { useCallback, useContext, useRef, useState } from "react";
import { useFinancialDocumentFinalizeAccrualImportMutation } from "@api/financialDocument.api";
import { AccrualImporterContext } from "./accrualImporter.context";

interface Params {
    onImportStarted: (financialDocumentIds: number[]) => void;
    onImportError: (financialDocumentIds: number[], error: Error) => void;
}

export function useConfirmAccrualDocuments({
    onImportStarted,
    onImportError,
}: Params) {
    const { mode } = useContext(AccrualImporterContext);
    const finalizeAccrualImport =
        useFinancialDocumentFinalizeAccrualImportMutation(mode);
    const { mutateAsync } = finalizeAccrualImport;
    const inFlightIdsRef = useRef(new Set<number>());
    const [inFlightDocumentIds, setInFlightDocumentIds] = useState<number[]>(
        [],
    );

    const confirmDocuments = useCallback(
        async (financialDocumentIds: number[]) => {
            const idsToConfirm = financialDocumentIds.filter(
                (documentId) => !inFlightIdsRef.current.has(documentId),
            );
            if (idsToConfirm.length === 0) {
                return;
            }

            idsToConfirm.forEach((documentId) =>
                inFlightIdsRef.current.add(documentId),
            );
            setInFlightDocumentIds(Array.from(inFlightIdsRef.current));

            try {
                await mutateAsync(idsToConfirm);
                onImportStarted(idsToConfirm);
            } catch (error) {
                onImportError(
                    idsToConfirm,
                    error instanceof Error
                        ? error
                        : new Error("Failed to start import"),
                );
            } finally {
                idsToConfirm.forEach((documentId) =>
                    inFlightIdsRef.current.delete(documentId),
                );
                setInFlightDocumentIds(Array.from(inFlightIdsRef.current));
            }
        },
        [mutateAsync, onImportStarted, onImportError],
    );

    return {
        confirmDocuments,
        isConfirming: finalizeAccrualImport.isPending,
        inFlightDocumentIds,
    };
}
